import { createColumnHelper } from '@tanstack/react-table';
import { RecordCountCell } from '@/components/schedules/record-count-cell';
import { TextCell } from '@/components/schedules/text-cell';
import { type DateBucket, itemsInBucket } from '@/utils/schedule-buckets';
import type { ScheduleRow } from '@/utils/schedule-records';

export interface HierarchyColumn {
  id: number;
  label: string;
}

interface ScheduleColumnsOptions {
  hierarchyTypes: HierarchyColumn[];
  buckets: DateBucket[];
  locale: string;
  labels: { form: string; description: string; schedule: string };
}

export const LEAD_COLUMN_IDS = ['form', 'description', 'schedule'] as const;

export const PINNED_COLUMN_IDS: string[] = [...LEAD_COLUMN_IDS];

export const hierarchyColumnId = (typeId: number) => `hierarchy-${typeId}`;

const LEAD_COLUMN_SIZE = 140;
const BUCKET_COLUMN_SIZE = 120;

const columnHelper = createColumnHelper<ScheduleRow>();

// Hierarchy columns are keyed by type id, so a multi-word type caption still finds its values.
export const buildScheduleColumns = ({ hierarchyTypes, buckets, locale, labels }: ScheduleColumnsOptions) => [
  ...hierarchyTypes.map((type) =>
    columnHelper.accessor((row) => row.hierarchy[String(type.id)] ?? '', {
      id: hierarchyColumnId(type.id),
      header: type.label,
      size: LEAD_COLUMN_SIZE,
      cell: ({ getValue }) => <TextCell value={getValue()} />,
    })
  ),
  columnHelper.accessor('form', {
    id: 'form',
    header: labels.form,
    size: LEAD_COLUMN_SIZE,
    cell: ({ getValue }) => <TextCell value={getValue()} />,
  }),
  columnHelper.accessor('description', {
    id: 'description',
    header: labels.description,
    size: LEAD_COLUMN_SIZE,
    cell: ({ getValue }) => <TextCell value={getValue()} />,
  }),
  columnHelper.accessor('schedule', {
    id: 'schedule',
    header: labels.schedule,
    size: LEAD_COLUMN_SIZE,
    cell: ({ getValue }) => <TextCell value={getValue()} />,
  }),
  ...buckets.map((bucket) =>
    columnHelper.display({
      id: `bucket-${bucket.id}`,
      header: () => <span className="block whitespace-normal leading-tight">{bucket.label}</span>,
      size: BUCKET_COLUMN_SIZE,
      enableHiding: false,
      enablePinning: false,
      cell: ({ row }) => <RecordCountCell records={itemsInBucket(row.original.records, bucket)} locale={locale} />,
    })
  ),
];

export const defaultColumnVisibility = (hierarchyTypes: HierarchyColumn[]) =>
  Object.fromEntries(hierarchyTypes.map((type) => [hierarchyColumnId(type.id), false]));
